import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ExpiredIcon } from '@/assets/icons/expired';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { ConfirmationStatus } from '@/components/authentication/statuses/confirmation';
import { ErrorStatus } from '@/components/authentication/statuses/error';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import Input, { ErrorText } from '@/components/basicComponents/input';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import axios from 'axios';
import { Helmet as Head } from 'react-helmet';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SET_LOGGED_IN_USER_PASSWORD_MUTATION } from './queries';
import { TwoFactorAuthentication, TwoFactorAuthenticationDescription, TwoFactorAuthenticationTitle } from './TwoFactorAuthentication';

interface IFormInput {
  newPassword: string;
  confirmedPassword: string;
}

const initialValidationItems = [
  {
    hint: 'Password must be 8 or more characters in length',
    type: 'length',
    correct: true
  },
  {
    hint: 'Password must contain at least 1 uppercase character',
    type: 'oneUpperCase',
    correct: true
  },
  {
    hint: 'Password must contain at least 1 lowercase character',
    type: 'oneLowerCase',
    correct: true
  },
  {
    hint: 'Password must NOT contain any spaces',
    type: 'space',
    correct: true
  }
];

enum CHANGE_PASSWORD_STATUS_ENUM {
  DEFAULT = 'default',
  CONFIRMATION = 'confirmation',
  EXPIRED = 'expired',
  SENT = 'sent',
  ERROR = 'error'
}

const VALIDATE_USERLESS_JWT_TOKEN_EXPIRATION_QUERY = `
  query validateUserlessJwtTokenExpiration($jwt: String!) {
    validateUserlessJwtTokenExpiration(jwt: $jwt)
  }
`;

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { user, isAuthenticated, changePassword, signOut } = useAuth();
  const {
    control,
    handleSubmit,
    getValues,
    watch,
    formState: { errors, isSubmitted }
  } = useForm<IFormInput>();

  const [searchParams, setSearchParams] = useSearchParams();

  const setNewPasswordToken = searchParams.get('setNewPasswordToken') || '';
  const resendWelcomeEmailToken = searchParams.get('resendWelcomeEmailToken') || '';

  const [validation, setValidation] = useState(initialValidationItems);
  const [validateUserlessJwtTokenExpirationLoading, setValidateUserlessJwtTokenExpirationLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<CHANGE_PASSWORD_STATUS_ENUM>(CHANGE_PASSWORD_STATUS_ENUM.DEFAULT);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [setLoggedInUserPassword, { loading: setLoggedInUserPasswordLoading }] = useMutation(SET_LOGGED_IN_USER_PASSWORD_MUTATION);

  useEffect(() => {
    if (!setNewPasswordToken) return;

    (async () => {
      try {
        setValidateUserlessJwtTokenExpirationLoading(true);
        const { data: setNewPasswordTokenData } = await axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/graphql`, {
          query: VALIDATE_USERLESS_JWT_TOKEN_EXPIRATION_QUERY,
          variables: {
            jwt: setNewPasswordToken
          }
        });

        if (setNewPasswordTokenData?.errors) {
          if (resendWelcomeEmailToken) {
            const { data: resendWelcomeEmailTokenData } = await axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/graphql`, {
              query: VALIDATE_USERLESS_JWT_TOKEN_EXPIRATION_QUERY,
              variables: {
                jwt: resendWelcomeEmailToken
              }
            });

            if (resendWelcomeEmailTokenData?.errors) {
              setStatus(CHANGE_PASSWORD_STATUS_ENUM.ERROR);
              return;
            }

            if (resendWelcomeEmailTokenData?.data?.validateUserlessJwtTokenExpiration === 'success') {
              setStatus(CHANGE_PASSWORD_STATUS_ENUM.EXPIRED);
              return;
            }
            return;
          }
          setStatus(CHANGE_PASSWORD_STATUS_ENUM.ERROR);
          return;
        }

        if (setNewPasswordTokenData?.data?.validateUserlessJwtTokenExpiration === 'success') {
          setStatus(CHANGE_PASSWORD_STATUS_ENUM.DEFAULT);
          return;
        }
      } catch (error) {
        console.log(error);
      } finally {
        setValidateUserlessJwtTokenExpirationLoading(false);
      }
    })();
  }, [setNewPasswordToken]);

  useEffect(() => {
    if (errors !== undefined) {
      const newValidation = validation.map((valid) =>
        errors.newPassword?.type === valid.type ? { ...valid, correct: false } : { ...valid, correct: true }
      );
      setValidation(newValidation);
    }
  }, [errors.newPassword?.type]);

  const checkValidConfirmedInput = () => watch('confirmedPassword') === watch('newPassword') && !!getValues('confirmedPassword');

  const handleStatusChange = (newStatus: CHANGE_PASSWORD_STATUS_ENUM) => {
    setStatus(newStatus);
  };

  const sendNewLink = async () => {
    try {
      setLoading(true);

      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/graphql`,
        {
          query: `
            mutation {
              resendWelcomeEmail
            }
          `
        },
        {
          headers: { Authorization: `Bearer ${resendWelcomeEmailToken}` }
        }
      );

      if (data?.errors) {
        setStatus(CHANGE_PASSWORD_STATUS_ENUM.ERROR);
        return;
      }

      setStatus(CHANGE_PASSWORD_STATUS_ENUM.SENT);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    signOut();
    navigate('/');
  };

  const changePasswordInLoggedInUser = async (data: IFormInput) => {
    try {
      setErrorMessage(null);
      setLoading(true);
      await setLoggedInUserPassword({
        variables: {
          data: {
            password: data.newPassword
          }
        }
      });

      if (!user.mobileNumberCountryCode && !user.mobileNumber) {
        navigate('/two-factor-authentication');
        return;
      }

      handleStatusChange(CHANGE_PASSWORD_STATUS_ENUM.CONFIRMATION);
    } catch (err: any) {
      console.error(err);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  const changePasswordInLoggedOutUser = async (data: IFormInput) => {
    try {
      setErrorMessage(null);
      setLoading(true);

      const { data: emailLinkSetPasswordData } = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/graphql`,
        {
          query: `
            mutation($data: SetUserPasswordInput!){
              emailLinkSetPassword(data: $data)
            }
          `,
          variables: {
            data: {
              password: data.newPassword
            }
          }
        },
        {
          headers: { Authorization: `Bearer ${setNewPasswordToken}` }
        }
      );

      if (emailLinkSetPasswordData?.errors) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      handleStatusChange(CHANGE_PASSWORD_STATUS_ENUM.CONFIRMATION);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: IFormInput): Promise<void> => {
    if (isAuthenticated && user && !setNewPasswordToken) {
      await changePasswordInLoggedInUser(data);
      return;
    }

    if (setNewPasswordToken) {
      await changePasswordInLoggedOutUser(data);
      return;
    }
  };

  if (validateUserlessJwtTokenExpirationLoading) return <LoaderOnWholeScreen />;

  const getContentByStatus = () => {
    switch (status) {
      case CHANGE_PASSWORD_STATUS_ENUM.EXPIRED:
        return (
          <>
            <TwoFactorAuthentication>
              <ExpiredIcon width={175} height={90} />
              <TwoFactorAuthenticationTitle>Expired Link</TwoFactorAuthenticationTitle>
              <TwoFactorAuthenticationDescription>
                The link for this download has expired. We will send you a fresh link to your email address.
              </TwoFactorAuthenticationDescription>
            </TwoFactorAuthentication>
            <Button size="md" onClick={sendNewLink} styleType={loading ? 'disabled' : 'default'} isLoading={loading}>
              Send New Link
            </Button>
          </>
        );
      case CHANGE_PASSWORD_STATUS_ENUM.SENT:
        return (
          <div>
            <TwoFactorAuthentication>
              <ExpiredIcon width={175} height={90} />
              <TwoFactorAuthenticationTitle>Expired Link</TwoFactorAuthenticationTitle>
              <TwoFactorAuthenticationDescription>
                The link for this download has expired. We will send you a fresh link to your email address.
              </TwoFactorAuthenticationDescription>
            </TwoFactorAuthentication>
            <BannerWrapper>
              <Banner
                title="Sent!"
                description="Your new link has been sent."
                icon={<ApprovedIcon fill={themeVar('context.light')} />}
                bgColor={themeVar('context.success')}
              />
            </BannerWrapper>
          </div>
        );
      case CHANGE_PASSWORD_STATUS_ENUM.CONFIRMATION:
        return <ConfirmationStatus />;
      case CHANGE_PASSWORD_STATUS_ENUM.ERROR:
        return <ErrorStatus />;
      default:
        return (
          <ChangePasswordForm noValidate onSubmit={handleSubmit(onSubmit)}>
            <FormLabel>Set Password</FormLabel>
            {errorMessage && (
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            )}
            <InputWrapper>
              <Controller
                name="newPassword"
                control={control}
                rules={{
                  validate: {
                    length: (v: string) => v?.length >= 8,
                    oneUpperCase: (v: string) => !!v.match(/[A-Z]/),
                    oneLowerCase: (v: string) => !!v.match(/[a-z]/),
                    space: (v: string) => !v.match(/\s/)
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    label="New Password"
                    value={value || ''}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    type="password"
                    errorText={error?.message}
                    size="lg"
                    withPasswordIcon
                    isRequiredField
                  />
                )}
              />
              {getValues('newPassword') && (
                <ValidationHints>
                  {validation.map(({ hint, correct }) => (
                    <ValidationHint key={hint} color={correct ? themeVar('font.base') : themeVar('context.error')}>
                      {hint}
                    </ValidationHint>
                  ))}
                </ValidationHints>
              )}
            </InputWrapper>
            <InputWrapper>
              <Controller
                name="confirmedPassword"
                control={control}
                rules={{
                  validate: {
                    theSame: (v: string) => v === getValues('newPassword')
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    label="Confirm New Password"
                    value={value || ''}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    type="password"
                    errorText={error?.message}
                    size="lg"
                    withPasswordIcon
                    isRequiredField
                  />
                )}
              />
              {!checkValidConfirmedInput() && isSubmitted ? <ErrorText size="md">Please make sure your passwords match</ErrorText> : null}
            </InputWrapper>

            <Divider />
            <Button
              type="submit"
              size="lg"
              width="max-content"
              styleType={!!Object.keys(errors).length || loading ? 'disabled' : 'default'}
              isLoading={loading}
            >
              Save
            </Button>
          </ChangePasswordForm>
        );
    }
  };

  return (
    <>
      <Head>
        <title>Change Password</title>
      </Head>
      {getContentByStatus()}
    </>
  );
};

export default ChangePasswordPage;

const ChangePasswordForm = styled.form`
  max-width: 400px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
`;

const FormLabel = styled.p`
  text-align: center;
  font-size: 33px;
  font-weight: 400;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;

const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${themeVar('border.base')};
`;

const ValidationHints = styled.ul`
  margin-left: 25px;
  list-style-type: square;
`;

const ValidationHint = styled.li<{ color: string }>`
  color: ${({ color }) => color};
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
`;

const BannerWrapper = styled.div`
  margin-top: 30px;
`;
